/**
 * User Block Component
 */
import React from "react";

import { Grid } from "@mui/material";
import { useAppState } from "Context/StateContext";
import { useTranslation } from "react-i18next";
import { FiCreditCard } from "react-icons/fi";
import {
  HiOutlineDocumentText,
  HiOutlineLogout,
  HiOutlineQuestionMarkCircle,
  HiOutlineUser,
} from "react-icons/hi";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useHistory } from "react-router";

import { TypographyButton } from "../../../components/SignUp/styled";
import { LAST_PAGE, LINKS } from "../../../constants";
import { Routes } from "../../../util/enums";
import { CompanyNameAndIconContainer } from "./CompanyNameAndIconContainer";
import useStyles, {
  ButtonLink,
  CompanyUserAndEmailWrapper,
  Divider,
  GridItemContainer,
  GridItemLink,
  UserProfileEmail,
  UserProfileImage,
  UserProfileMenu,
  UserProfileMenuHeader,
  UserProfileName,
  UserProfileSectionTitle,
} from "./styles";

interface UserBlockProfileMenuProps {
  billingIsAllowed: boolean;
  userProfileMenu?: HTMLElement | null;
  userCompanyMenu?: HTMLElement | null;
  avatarUrl: string;
  handleCloseProfileMenu: () => void;
  handleOpenCompanyMenu: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const UserBlockProfileMenu = ({
  billingIsAllowed,
  userProfileMenu,
  userCompanyMenu,
  avatarUrl,
  handleCloseProfileMenu,
  handleOpenCompanyMenu,
}: UserBlockProfileMenuProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { userLogout, currentUser } = useAppState();

  const openUrl = (url: string, external = false) => {
    if (external) {
      window.open(url, "_blank");
    } else {
      history.push(`/app/${url}`);
    }
    handleCloseProfileMenu();
  };

  const logoutUser = () => {
    userLogout();
  };

  const renderBillingButton = () => {
    if (billingIsAllowed) {
      return (
        <GridItemLink isLogout={false} item>
          <ButtonLink
            onClick={() => {
              openUrl(Routes.BILLING);
              handleCloseProfileMenu();
            }}
            isLogout={false}
            data-cy="billingLink"
          >
            <FiCreditCard />
            {translate("shipeeziBilling")}
          </ButtonLink>
        </GridItemLink>
      );
    }

    return null;
  };

  return (
    <UserProfileMenu
      anchorEl={userProfileMenu}
      keepMounted={false}
      open={Boolean(userProfileMenu)}
      onClose={handleCloseProfileMenu}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ horizontal: "center", vertical: "top" }}
      PaperProps={{
        className: classes.menuPaperProps,
      }}
    >
      <Grid container direction="column" data-cy="userProfileMenuContainer">
        <UserProfileMenuHeader item container direction="row" className={"user-profile-top"}>
          <UserProfileImage src={avatarUrl} data-cy="userProfileMenuAvatar" alt="user profile" />
          <CompanyUserAndEmailWrapper item>
            <UserProfileName data-cy="userProfileMenuName">
              {currentUser ? `${translate("hello")} ${currentUser?.name}!` : translate("unknown")}
            </UserProfileName>
            <UserProfileEmail data-cy="userProfileMenuEmail">
              {currentUser ? currentUser?.email : translate("unknown")}
            </UserProfileEmail>
          </CompanyUserAndEmailWrapper>
        </UserProfileMenuHeader>

        <GridItemContainer container direction="column">
          <UserProfileSectionTitle>{translate("company")}</UserProfileSectionTitle>

          {currentUser && (
            <>
              <TypographyButton
                onClick={handleOpenCompanyMenu}
                data-cy="userProfileMenuCompanyOption"
                as={"div"}
              >
                <CompanyNameAndIconContainer
                  company={currentUser?.company}
                  displayChevron={true}
                  showCompanyOptions={Boolean(userCompanyMenu)}
                />
              </TypographyButton>
            </>
          )}

          <Divider />

          <GridItemLink isLogout={false} item>
            <ButtonLink
              onClick={() => {
                window.localStorage.setItem(LAST_PAGE, window.location.pathname);
                openUrl(Routes.PROFILE);
                handleCloseProfileMenu();
              }}
              isLogout={false}
              data-cy="userProfileMenuProfileLink"
            >
              <HiOutlineUser />
              {translate("userProfile")}
            </ButtonLink>
          </GridItemLink>
          {renderBillingButton()}
          <GridItemLink isLogout={false} item>
            <ButtonLink
              isLogout={false}
              onClick={() => openUrl("https://help.shipeezi.com/", true)}
              data-cy="userProfileMenuHelpCenter"
            >
              <HiOutlineQuestionMarkCircle />
              {translate("helpCenter")}
            </ButtonLink>
          </GridItemLink>
          <GridItemLink isLogout={false} item>
            <ButtonLink
              isLogout={false}
              onClick={() => openUrl(LINKS.termsOfUse, true)}
              data-cy="userProfileMenuTermsAndConditions"
            >
              <HiOutlineDocumentText />
              {translate("termsAndConditions")}
            </ButtonLink>
          </GridItemLink>

          <Divider />

          <GridItemLink isLogout={true} item>
            <ButtonLink
              isLogout={true}
              onClick={() => logoutUser()}
              data-cy="userProfileMenuLogout"
            >
              <HiOutlineLogout />
              {translate("logout")}
            </ButtonLink>
          </GridItemLink>
        </GridItemContainer>
      </Grid>
    </UserProfileMenu>
  );
};
